// Localização: frontend/src/components/Prontuario/PacienteEquipamentosTab.jsx (Versão 1.0.0)

import React, { useState, useEffect, useMemo } from 'react';
import { getHistoricoEquipamentosByPaciente } from '../../api/api';
import { usePacienteProntuario } from '../../hooks/data/usePacienteProntuario';
import Title from '../Layout/Title';
import LoadingIndicator from '../Common/LoadingIndicator';
import Alert from '../UI/Alert';
import PrimaryButton from '../Common/PrimaryButton';
import { Download, Package, Calendar, MapPin, Wrench, AlertCircle, Clock, CheckCircle, XCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import { useNotification } from '../../context/NotificationContext';

const PacienteEquipamentosTab = ({ pacienteId }) => {
    const { warning } = useNotification();
    const [equipamentos, setEquipamentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { data: pacienteData } = usePacienteProntuario(pacienteId);

    useEffect(() => {
        fetchEquipamentos();
    }, [pacienteId]);

    const fetchEquipamentos = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getHistoricoEquipamentosByPaciente(pacienteId);
            setEquipamentos(response.data || []);
        } catch (err) {
            console.error('Erro ao buscar histórico de equipamentos:', err);
            setError(err.response?.data?.message || 'Erro ao carregar histórico de equipamentos.');
        } finally {
            setLoading(false);
        }
    };

    // Separar equipamentos ativos e histórico
    const { equipamentosAtivos, equipamentosHistorico } = useMemo(() => {
        const ativos = equipamentos.filter(eq => eq.is_ativo === true || eq.data_fim_uso === null);
        const historico = equipamentos.filter(eq => eq.is_ativo === false || eq.data_fim_uso !== null);
        return { equipamentosAtivos: ativos, equipamentosHistorico: historico };
    }, [equipamentos]);

    const formatarData = (dataString) => {
        if (!dataString) return 'N/A';
        try {
            return new Date(dataString).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return 'N/A';
        }
    };

    const gerarPDFEquipamentos = () => {
        if (!pacienteData?.paciente || equipamentos.length === 0) {
            warning('Não há equipamentos para gerar o PDF.');
            return;
        }

        const doc = new jsPDF();
        const paciente = pacienteData.paciente;
        const dataAtual = new Date().toLocaleDateString('pt-BR');

        // Cabeçalho
        doc.setFontSize(18);
        doc.setTextColor(16, 185, 129); // emerald-600
        doc.text('RELATÓRIO DE EQUIPAMENTOS', 105, 20, { align: 'center' });
        
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text('Histórico Completo', 105, 30, { align: 'center' });

        // Informações do Paciente
        doc.setFontSize(14);
        doc.text('DADOS DO PACIENTE', 14, 45);
        
        doc.setFontSize(11);
        doc.text(`Nome: ${paciente.nome_completo}`, 14, 55);
        doc.text(`Data de Nascimento: ${paciente.data_nascimento_formatada || 'N/A'}`, 14, 62);
        doc.text(`Condição Médica: ${paciente.condicao_medica || 'Não informada'}`, 14, 69);
        doc.text(`Data do Relatório: ${dataAtual}`, 14, 76);

        // Equipamentos Ativos
        let yPos = 90;
        
        if (equipamentosAtivos.length > 0) {
            doc.setFontSize(14);
            doc.text('EQUIPAMENTOS ATIVOS', 14, yPos);
            yPos += 10;

            // Cabeçalho da tabela
            doc.setFontSize(10);
            doc.setDrawColor(16, 185, 129);
            doc.setFillColor(16, 185, 129);
            doc.rect(14, yPos, 182, 8, 'F');
            doc.setTextColor(255, 255, 255);
            doc.text('Equipamento', 16, yPos + 6);
            doc.text('Marca', 70, yPos + 6);
            doc.text('Nº Série', 120, yPos + 6);
            doc.text('Data Início', 165, yPos + 6);
            
            yPos += 8;
            doc.setTextColor(0, 0, 0);

            equipamentosAtivos.forEach((equip, index) => {
                if (yPos > 240) {
                    doc.addPage();
                    yPos = 20;
                }

                const isEven = index % 2 === 0;
                if (isEven) {
                    doc.setFillColor(245, 245, 245);
                    doc.rect(14, yPos, 182, 8, 'F');
                }

                doc.setDrawColor(200, 200, 200);
                doc.rect(14, yPos, 182, 8);

                doc.setFontSize(9);
                doc.text(equip.nome_equipamento || 'N/A', 16, yPos + 6);
                doc.text(equip.marca || 'N/A', 70, yPos + 6);
                doc.text(equip.numero_serie || 'N/A', 120, yPos + 6);
                doc.text(formatarData(equip.data_inicio_uso), 165, yPos + 6);

                yPos += 8;
            });
            yPos += 5;
        }

        // Histórico de Equipamentos
        if (equipamentosHistorico.length > 0) {
            if (yPos > 230) {
                doc.addPage();
                yPos = 20;
            }

            doc.setFontSize(14);
            doc.text('HISTÓRICO DE EQUIPAMENTOS', 14, yPos);
            yPos += 10;

            // Cabeçalho da tabela
            doc.setFontSize(10);
            doc.setDrawColor(107, 114, 128); // gray-500
            doc.setFillColor(107, 114, 128);
            doc.rect(14, yPos, 182, 8, 'F');
            doc.setTextColor(255, 255, 255);
            doc.text('Equipamento', 16, yPos + 6);
            doc.text('Marca', 60, yPos + 6);
            doc.text('Início', 105, yPos + 6);
            doc.text('Fim', 145, yPos + 6);
            doc.text('Status', 175, yPos + 6);
            
            yPos += 8;
            doc.setTextColor(0, 0, 0);

            equipamentosHistorico.forEach((equip, index) => {
                if (yPos > 240) {
                    doc.addPage();
                    yPos = 20;
                }

                const isEven = index % 2 === 0;
                if (isEven) {
                    doc.setFillColor(245, 245, 245);
                    doc.rect(14, yPos, 182, 8, 'F');
                }

                doc.setDrawColor(200, 200, 200);
                doc.rect(14, yPos, 182, 8);

                doc.setFontSize(9);
                doc.text((equip.nome_equipamento || 'N/A').substring(0, 18), 16, yPos + 6);
                doc.text((equip.marca || 'N/A').substring(0, 12), 60, yPos + 6);
                doc.text(formatarData(equip.data_inicio_uso), 105, yPos + 6);
                doc.text(formatarData(equip.data_fim_uso), 145, yPos + 6);
                doc.text('Desvinculado', 175, yPos + 6);

                yPos += 8;
            });
        }

        // Adicionar campos de assinatura no final
        yPos += 15;
        
        // Verificar se precisa de nova página para as assinaturas
        if (yPos > 200) {
            doc.addPage();
            yPos = 50;
        }

        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text('ASSINATURAS', 14, yPos);
        yPos += 10;

        // Linha divisória
        doc.setDrawColor(200, 200, 200);
        doc.line(14, yPos, 196, yPos);
        yPos += 15;

        // Campo de assinatura - Quem Entrega
        doc.setFontSize(10);
        doc.text('QUEM ENTREGA:', 14, yPos);
        yPos += 8;
        
        // Linha para assinatura
        doc.setDrawColor(100, 100, 100);
        doc.line(14, yPos, 100, yPos);
        yPos += 5;
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text('Nome e Assinatura', 14, yPos);
        yPos += 20;

        // Campo de assinatura - Quem Recebe
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text('QUEM RECEBE:', 14, yPos);
        yPos += 8;
        
        // Linha para assinatura
        doc.setDrawColor(100, 100, 100);
        doc.line(14, yPos, 100, yPos);
        yPos += 5;
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text('Nome e Assinatura', 14, yPos);
        yPos += 10;

        // Data e local
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text(`Data: ${dataAtual}`, 14, yPos);
        yPos += 6;
        doc.text('Local: _____________________________', 14, yPos);

        // Rodapé
        const totalPages = doc.internal.pages.length - 1;
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(128, 128, 128);
            doc.text(
                `Página ${i} de ${totalPages} - Gerado em ${dataAtual}`,
                105,
                285,
                { align: 'center' }
            );
        }

        // Download
        const nomeArquivo = `Historico_Equipamentos_${paciente.nome_completo.replace(/\s+/g, '_')}_${dataAtual.replace(/\//g, '-')}.pdf`;
        doc.save(nomeArquivo);
    };

    if (loading) {
        return <LoadingIndicator message="Carregando equipamentos..." />;
    }

    if (error) {
        return <Alert type="error" message={error} />;
    }

    const EquipamentoCard = ({ equipamento, isAtivo = true }) => (
        <div
            className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border p-6 ${
                isAtivo ? 'border-emerald-200' : 'border-gray-300 opacity-90'
            }`}
        >
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-lg ${isAtivo ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                        {isAtivo ? (
                            <Package className={`w-6 h-6 ${isAtivo ? 'text-emerald-600' : 'text-gray-600'}`} />
                        ) : (
                            <Clock className="w-6 h-6 text-gray-600" />
                        )}
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">
                            {equipamento.nome_equipamento}
                        </h3>
                        <span
                            className={`inline-block px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                                isAtivo
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                            }`}
                        >
                            {isAtivo ? 'Ativo' : 'Desvinculado'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="space-y-2 text-sm text-gray-600">
                {equipamento.marca && (
                    <div className="flex items-center">
                        <Wrench className={`w-4 h-4 mr-2 flex-shrink-0 ${isAtivo ? 'text-emerald-600' : 'text-gray-500'}`} />
                        <span>
                            <strong>Marca:</strong> {equipamento.marca}
                        </span>
                    </div>
                )}

                {equipamento.numero_serie && (
                    <div className="flex items-center">
                        <AlertCircle className={`w-4 h-4 mr-2 flex-shrink-0 ${isAtivo ? 'text-emerald-600' : 'text-gray-500'}`} />
                        <span>
                            <strong>Nº Série:</strong> {equipamento.numero_serie}
                        </span>
                    </div>
                )}

                {equipamento.data_inicio_uso && (
                    <div className="flex items-center">
                        <Calendar className={`w-4 h-4 mr-2 flex-shrink-0 ${isAtivo ? 'text-emerald-600' : 'text-gray-500'}`} />
                        <span>
                            <strong>Início de uso:</strong>{' '}
                            {formatarData(equipamento.data_inicio_uso)}
                        </span>
                    </div>
                )}

                {!isAtivo && equipamento.data_fim_uso && (
                    <div className="flex items-center">
                        <XCircle className="w-4 h-4 mr-2 text-red-500 flex-shrink-0" />
                        <span>
                            <strong>Fim de uso:</strong>{' '}
                            {formatarData(equipamento.data_fim_uso)}
                        </span>
                    </div>
                )}

                {isAtivo && equipamento.local_vinculado && (
                    <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2 text-emerald-600 flex-shrink-0" />
                        <span className="truncate">
                            <strong>Local:</strong> {equipamento.local_vinculado}
                        </span>
                    </div>
                )}

                {equipamento.descricao && (
                    <div className="pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500">
                            <strong>Descrição:</strong> {equipamento.descricao}
                        </p>
                    </div>
                )}

                {isAtivo && equipamento.data_proxima_manutencao && (
                    <div className="pt-2 border-t border-gray-200">
                        <p className="text-xs text-amber-600">
                            <strong>Próxima Manutenção:</strong>{' '}
                            {formatarData(equipamento.data_proxima_manutencao)}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <Title text="Histórico de Equipamentos" size="small" />
                {equipamentos.length > 0 && (
                    <PrimaryButton
                        onClick={gerarPDFEquipamentos}
                        icon={<Download className="w-4 h-4" />}
                        size="sm"
                    >
                        Baixar PDF Completo
                    </PrimaryButton>
                )}
            </div>

            {equipamentos.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600 text-lg font-medium">
                        Nenhum equipamento registrado para este paciente.
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                        O histórico de equipamentos aparecerá aqui quando forem associados ao paciente.
                    </p>
                </div>
            ) : (
                <>
                    {/* Seção de Equipamentos Ativos */}
                    {equipamentosAtivos.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                                <h2 className="text-xl font-bold text-gray-900">
                                    Equipamentos Ativos ({equipamentosAtivos.length} {equipamentosAtivos.length === 1 ? 'equipamento' : 'equipamentos'})
                                </h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {equipamentosAtivos.map((equipamento) => (
                                    <EquipamentoCard 
                                        key={`ativo-${equipamento.vinculo_id || equipamento.id}`}
                                        equipamento={equipamento} 
                                        isAtivo={true}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Seção de Histórico */}
                    {equipamentosHistorico.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center space-x-2">
                                <Clock className="w-5 h-5 text-gray-600" />
                                <h2 className="text-xl font-bold text-gray-900">
                                    Histórico ({equipamentosHistorico.length} {equipamentosHistorico.length === 1 ? 'equipamento' : 'equipamentos'})
                                </h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {equipamentosHistorico.map((equipamento) => (
                                    <EquipamentoCard 
                                        key={`historico-${equipamento.vinculo_id || equipamento.id}`}
                                        equipamento={equipamento} 
                                        isAtivo={false}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Resumo */}
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-6">
                        <div className="flex items-start">
                            <AlertCircle className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-emerald-900">
                                    Resumo: {equipamentosAtivos.length} ativo(s) e {equipamentosHistorico.length} no histórico
                                </p>
                                <p className="text-xs text-emerald-700 mt-1">
                                    Este relatório pode ser usado para documentação de encerramento de contrato.
                                    Clique em "Baixar PDF Completo" para gerar o documento completo com histórico.
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PacienteEquipamentosTab;

